import type { UUIDv4 } from '@global-types/uuid';
import type { Media } from '@models/groups/app';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { AppData } from '@global-types/appData.ts';
import { useAppDataStore } from './useAppDataStore';

export const useAuthStore = defineStore('auth', () => {
  const appDataStore = useAppDataStore();

  const {
    user,
    isImpersonated,
    organizationUsers,
    impersonatedPipedriveUser,
  } = storeToRefs(appDataStore);

  const sessionUser = ref<AppData['user'] | null>(user.value ?? null);

  const isLoggedIn = computed<boolean>(() => !!sessionUser.value);

  const resetSessionUser = (): void => {
    sessionUser.value = user.value;
  };

  const clearSessionUser = (): void => {
    sessionUser.value = null;
  };

  const setProfilePictureUuid = (uuid: UUIDv4 | null): void => {
    if (!sessionUser.value) {
      throw new Error('User not logged in.');
    }

    sessionUser.value.profile_picture = uuid ? {
      uuid,
      description: sessionUser.value.profile_picture?.description ?? '',
    } as Media : null;
  };

  const profilePictureUuid = computed<UUIDv4 | null>({
    get: (): UUIDv4 | null => sessionUser.value?.profile_picture?.uuid ?? null,
    set: (uuid: UUIDv4 | null): void => setProfilePictureUuid(uuid),
  });

  return {
    user,
    sessionUser,
    organizationUsers,
    isImpersonated,
    isLoggedIn,
    impersonatedPipedriveUser,
    profilePictureUuid,
    resetSessionUser,
    clearSessionUser,
  };
});
